// StaffReview.js
import React, { useState, useRef, useContext, useEffect } from "react";
import { addDoc, collection, updateDoc, deleteDoc, doc, getDocs } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { EnvContext } from "../../../context/EnvContext";
import { useFirestore } from "../../firebase/FirestoreContext";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import config from "../../../config";
import { toast } from "react-toastify";
import { Clear } from "@mui/icons-material";
import { arrayMoveImmutable } from "array-move";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

const SortableImage = SortableElement(({ image, index, onDelete }) => (
  <div style={styles.imageContainer}>
    <div style={styles.imageWrapper}>
      <img src={image} alt={`review-${index}`} style={styles.thumbnail} />
    </div>
    <div onClick={() => onDelete(index)} style={styles.deleteText}>
      Delete
    </div>
  </div>
));

const SortableImageList = SortableContainer(({ images, onDelete }) => (
  <div style={styles.imageList}>
    {images && images.map((image, index) => <SortableImage key={`image-${index}`} index={index} image={image} onDelete={onDelete} />)}
  </div>
));

const StaffReview = () => {
  const { id } = useParams(); // product id
  const navigate = useNavigate();
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();

  // Determine the collection path based on the environment.
  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  // Reviews subcollection path:
  const reviewCollectionRef = collection(db, `${collectionPath}Products/ProductsList/DataBase`, id, "ReviewsStaff");

  // Form states
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [reviewImageFiles, setReviewImageFiles] = useState([]); // new images to upload
  const [avatarImageFile, setAvatarImageFile] = useState(null);
  // State for existing images when editing
  const [existingReviewImages, setExistingReviewImages] = useState([]);
  const [existingAvatarImageUrl, setExistingAvatarImageUrl] = useState("");
  const [reviews, setReviews] = useState([]);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Refs for file inputs
  const reviewImageInputRef = useRef(null);
  const avatarImageInputRef = useRef(null);

  // Fetch reviews on component mount and after updates
  const fetchReviews = async () => {
    try {
      const querySnapshot = await getDocs(reviewCollectionRef);
      const reviewsList = [];
      querySnapshot.forEach((docSnap) => {
        reviewsList.push({ id: docSnap.id, ...docSnap.data() });
      });
      setReviews(reviewsList);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, reviewCollectionRef]);

  // Helper function to upload multiple files
  const uploadFiles = async (files, folder) => {
    const urls = [];
    for (const file of files) {
      const fileRef = ref(storage, `${folder}/${id}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const url = await getDownloadURL(snapshot.ref);
      urls.push(url);
    }
    return urls;
  };

  // Handle file changes for new review images
  const handleReviewImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setReviewImageFiles(Array.from(e.target.files));
    }
  };

  const handleAvatarImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarImageFile(e.target.files[0]);
    }
  };

  // Remove an image from the existing review images (during editing)
  const removeExistingReviewImage = (index) => {
    const updated = existingReviewImages.filter((_, i) => i !== index);
    setExistingReviewImages(updated);
  };

  // Remove the existing avatar image (during editing)
  const removeExistingAvatar = () => {
    setExistingAvatarImageUrl("");
  };

  // Handle form submission for both creating and updating a review
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Upload new review images if provided
      let newReviewImageUrls = [];
      if (reviewImageFiles.length > 0) {
        newReviewImageUrls = await uploadFiles(reviewImageFiles, "reviewImages");
      }

      // Upload new avatar image if provided
      let newAvatarImageUrl = "";
      if (avatarImageFile) {
        const avatarRef = ref(storage, `reviewerAvatars/${id}/${Date.now()}_${avatarImageFile.name}`);
        const snapshot = await uploadBytes(avatarRef, avatarImageFile);
        newAvatarImageUrl = await getDownloadURL(snapshot.ref);
      }

      let reviewData = {};
      if (editingReviewId) {
        // Find the review being edited to use its original data if needed.
        const currentReview = reviews.find((r) => r.id === editingReviewId) || {};
        reviewData = {
          reviewTitle,
          reviewText,
          reviewerName,
          // Combine existing review images (after removals) with any new uploads.
          reviewImageUrls: [...existingReviewImages, ...newReviewImageUrls],
          // Use new avatar if uploaded; otherwise, use the existing avatar (which might be empty if removed).
          avatarImageUrl: newAvatarImageUrl || existingAvatarImageUrl || "",
          updatedAt: new Date(),
        };
        const reviewDocRef = doc(reviewCollectionRef, editingReviewId);
        await updateDoc(reviewDocRef, reviewData);
        toast.success("Review updated successfully");
      } else {
        // Creating new review
        reviewData = {
          reviewTitle,
          reviewText,
          reviewerName,
          reviewImageUrls: newReviewImageUrls, // may be empty if no images selected
          avatarImageUrl: newAvatarImageUrl,
          createdAt: new Date(),
        };
        await addDoc(reviewCollectionRef, reviewData);
        toast.success("Review added successfully");
      }

      // Reset the form and state
      setReviewTitle("");
      setReviewText("");
      setReviewerName("");
      setReviewImageFiles([]);
      setAvatarImageFile(null);
      setExistingReviewImages([]);
      setExistingAvatarImageUrl("");
      setEditingReviewId(null);
      if (reviewImageInputRef.current) reviewImageInputRef.current.value = "";
      if (avatarImageInputRef.current) avatarImageInputRef.current.value = "";
      fetchReviews();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Error submitting review");
    } finally {
      setLoading(false);
    }
  };

  // Handle editing a review (populate form with review data)
  const handleEdit = (review) => {
    setEditingReviewId(review.id);
    setReviewTitle(review.reviewTitle);
    setReviewText(review.reviewText);
    setReviewerName(review.reviewerName);
    // Set existing images for editing
    setExistingReviewImages(review.reviewImageUrls || []);
    setExistingAvatarImageUrl(review.avatarImageUrl || "");
    // Clear any new file inputs
    setReviewImageFiles([]);
    setAvatarImageFile(null);
    if (reviewImageInputRef.current) reviewImageInputRef.current.value = "";
    if (avatarImageInputRef.current) avatarImageInputRef.current.value = "";
  };

  // Handle deleting a review
  const handleDelete = async (reviewId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(reviewCollectionRef, reviewId));
      toast.success("Review deleted successfully");
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      // If the deleted review is being edited, reset form.
      if (editingReviewId === reviewId) {
        setEditingReviewId(null);
        setReviewTitle("");
        setReviewText("");
        setReviewerName("");
        setExistingReviewImages([]);
        setExistingAvatarImageUrl("");
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      toast.error("Error deleting review");
    } finally {
      setLoading(false);
    }
  };

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (editingReviewId) {
      const newImages = arrayMoveImmutable(existingReviewImages, oldIndex, newIndex);
      setExistingReviewImages(newImages);
      try {
        const reviewDocRef = doc(reviewCollectionRef, editingReviewId);
        await updateDoc(reviewDocRef, { reviewImageUrls: newImages });
        toast.success("Image order updated successfully");
      } catch (error) {
        console.error("Error updating image order:", error);
        toast.error("Failed to update image order");
      }
    }
  };

  return (
    <div style={styles.wrapper}>
      <style>
        {`
            .ql-editor {
            background: white;
            }
            .ql-toolbar {
            }`}
      </style>
      <form onSubmit={handleSubmit} style={styles.formContainer}>
        <h2>Staff Review</h2>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Review Title:</label>
          <input
            type="text"
            value={reviewTitle}
            onChange={(e) => setReviewTitle(e.target.value)}
            style={styles.formInput}
            placeholder="Enter review title"
            required
          />
        </div>

        <div style={{ ...styles.formElement }}>
          <label style={styles.formLabel}>Review:</label>
          <ReactQuill
            value={reviewText}
            onChange={setReviewText}
            style={{ ...styles.quillEditor, background: "white", marginBottom: 50 }}
            placeholder="Write your review..."
          />
        </div>

        {/* Existing review images (only when editing) */}
        {editingReviewId && existingReviewImages.length > 0 && (
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Existing Review Images:</label>
            <SortableImageList images={existingReviewImages} onSortEnd={onSortEnd} onDelete={removeExistingReviewImage} axis="xy" />
          </div>
        )}

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Upload New Review Images (multiple allowed):</label>
          <input type="file" accept="image/*" multiple onChange={handleReviewImageChange} ref={reviewImageInputRef} style={styles.formInput} />
        </div>

        {/* Existing avatar image (only when editing) */}
        {editingReviewId && existingAvatarImageUrl && (
          <div style={styles.formElement}>
            <label style={styles.formLabel}>Existing Reviewer Avatar:</label>
            <div style={styles.avatarContainer}>
              <img src={existingAvatarImageUrl} alt="existing-avatar" style={styles.avatarImage} />
              <button type="button" onClick={removeExistingAvatar} style={styles.removeButton}>
                <Clear style={{ fontSize: ".9rem" }} />
              </button>
            </div>
          </div>
        )}
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Reviewer Name:</label>
          <input
            type="text"
            value={reviewerName}
            onChange={(e) => setReviewerName(e.target.value)}
            style={styles.formInput}
            placeholder="Enter reviewer name"
            required
          />
        </div>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Upload Reviewer Avatar:</label>
          <input type="file" accept="image/*" onChange={handleAvatarImageChange} ref={avatarImageInputRef} style={styles.formInput} />
        </div>

        <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
          <button
            type="submit"
            style={{
              ...styles.button,
              backgroundColor: loading ? "silver" : "#478169",
            }}
            disabled={loading}
          >
            {loading ? "Submitting..." : editingReviewId ? "Update Review" : "Submit Review"}
          </button>
          {editingReviewId && (
            <button
              type="button"
              onClick={() => {
                // Cancel editing and reset form
                setEditingReviewId(null);
                setReviewTitle("");
                setReviewText("");
                setReviewerName("");
                setExistingReviewImages([]);
                setExistingAvatarImageUrl("");
                if (reviewImageInputRef.current) reviewImageInputRef.current.value = "";
                if (avatarImageInputRef.current) avatarImageInputRef.current.value = "";
              }}
              style={{
                ...styles.button,
                backgroundColor: "transparent",
                border: "1px solid black",
                color: "black",
                marginLeft: 10,
              }}
            >
              Cancel Editing
            </button>
          )}
        </div>
      </form>

      <div style={styles.listContainer}>
        <h3>Existing Reviews</h3>
        {reviews.length === 0 && <p>No reviews yet.</p>}
        {reviews.map((review) => (
          <div key={review.id} style={styles.reviewCard}>
            <div style={styles.reviewHeader}>
              <strong>{review.reviewTitle}</strong>
              <div>
                <button onClick={() => handleEdit(review)} style={styles.actionButton}>
                  Edit
                </button>
                <button onClick={() => handleDelete(review.id)} style={{ ...styles.actionButton, backgroundColor: "#a94442" }}>
                  Delete
                </button>
              </div>
            </div>
            <div dangerouslySetInnerHTML={{ __html: review.reviewText }} style={styles.reviewText} />
            {review.reviewImageUrls && review.reviewImageUrls.length > 0 && (
              <div style={styles.reviewImages}>
                {review.reviewImageUrls.map((imgUrl, index) => (
                  <img key={index} src={imgUrl} alt={`review-${index}`} style={styles.thumbnail} />
                ))}
              </div>
            )}
            {review.avatarImageUrl && (
              <div style={styles.avatarWrapper}>
                <img src={review.avatarImageUrl} alt="avatar" style={styles.avatarImage} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 30,
    padding: 20,
  },
  formContainer: {
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 15,
    border: "1px solid #ccc",
    borderRadius: 5,
    backgroundColor: "#f9f9f9",
    width: "100%",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    marginBottom: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px 15px",
    fontSize: 16,
    border: "0.5px solid #ccc",
    borderRadius: 5,
    width: "100%",
  },
  quillEditor: {
    height: 200,
    marginBottom: 10,
  },
  button: {
    padding: "0px",
    width: "150px",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 14,
    height: 50,
  },
  listContainer: {
    padding: 20,
    border: "1px solid #ccc",
    borderRadius: 5,
    backgroundColor: "#fff",
  },
  reviewCard: {
    borderBottom: "1px solid #ddd",
    paddingBottom: 10,
    marginBottom: 10,
  },
  reviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  actionButton: {
    marginLeft: 5,
    padding: "5px 10px",
    fontSize: 12,
    color: "white",
    backgroundColor: "#478169",
    border: "none",
    borderRadius: 3,
    cursor: "pointer",
  },
  reviewText: {
    marginTop: 10,
  },
  reviewImages: {
    display: "flex",
    gap: 10,
    marginTop: 10,
  },
  existingImagesContainer: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  imageWrapper: {
    position: "relative",
    display: "inline-block",
  },
  thumbnail: {
    width: 100,
    height: 100,
    objectFit: "cover",
    border: "1px solid #ccc",
    borderRadius: 5,
  },
  removeButton: {
    position: "absolute",
    top: 2,
    right: 2,
    backgroundColor: "black",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: 26,
    height: 26,
    cursor: "pointer",
    lineHeight: "18px",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
  avatarContainer: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  avatarImage: {
    width: 50,
    height: 50,
    borderRadius: "50%",
    objectFit: "cover",
    border: "1px solid #ccc",
  },
  avatarWrapper: {
    marginTop: 10,
  },
  imageList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  imageContainer: {
    marginBottom: 15,
    marginRight: 10,
  },
  imageWrapper: {
    display: "flex",
    alignItems: "center",
    marginBottom: 10,
    padding: 2,
    border: "1px solid silver",
  },
  deleteText: {
    fontSize: 12,
    color: "red",
    cursor: "pointer",
  },
};

export default StaffReview;
